import { Fragment, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SetupDataService from "../../services/setup.service";
import { useReportsStore } from "../../reports/useReportsStore";
import {
  Alert,
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ghostButtonNavy, inverseNavyButton, muiTextField, navyButton } from "../../styles/mui-overrides";
import DatePicker from "../../components/lib/SafeLeaseDatePicker";
import { useCalendlyEventListener } from "react-calendly";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { QueryResult, useQuery } from "@apollo/client";
import { Relationship } from "../../utilities/generated/gql-types";
import { getRelationship } from "../../queries";
import { useAuth } from "../../auth";
import { CalendlyButton } from "../../shared/CalendlyButton";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import { SafeLeaseFileDropzone } from "@safelease/components";
import FilePreviews from "./FilePreviews";
import PrivatePolicyDataService, { PrivatePolicyData } from "../../services/private-policy.service";
import AttachmentDataService from "../../services/attachments.service";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as Sentry from "@sentry/react";
import { PrivatePolicyEmailSettings } from "./PrivatePolicyEmailSettings";

interface Lease {
  id: number;
  unitName: string;
  tenantName: string;
  tenantEmail: string;
  unitId: number;
}

interface FormError {
  name: string;
  msg: string;
}

interface FormErrors {
  tenantName?: FormError;
  email?: FormError;
  moveInDate?: FormError;
  insuranceCompany?: FormError;
  policyNumber?: FormError;
  policyDeductible?: FormError;
  expiration?: FormError;
  attachment?: FormError;
  other?: FormError;
}

export function CreatePrivatePolicy() {
  const auth = useAuth();

  const relationshipId = useReportsStore((state) => state.relationshipId);
  const locations = useReportsStore((state) => state.locations);

  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [location, setLocation] = useState<any | null>(null);
  const [leases, setLeases] = useState<Lease[] | null>(null);
  const [selectedLeases, setSelectedLeases] = useState<Lease[]>([]);
  const [filteredLeases, setFilteredLeases] = useState<Lease[]>([]);
  const [isComplianceEnabled, setIsComplianceEnabled] = useState<boolean>(false);
  const [isTenantPolicyExpirationRemindersEnabled, setIsTenantPolicyExpirationRemindersEnabled] = useState<boolean>(false);
  const [unitMatchingMethod, setUnitMatchingMethod] = useState<"unitName" | "moveInDate">("unitName");

  // Admin settings visibility
  const showAdminSettings = auth.user?.isAdmin;

  // Private Policy Details
  const [tenantName, setTenantName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [moveInDate, setMoveInDate] = useState<Date | null>(null);
  const [insuranceCompany, setInsuranceCompany] = useState<string>("");
  const [policyNumber, setPolicyNumber] = useState<string>("");
  const [policyDeductible, setPolicyDeductible] = useState<string>("");
  const [expiration, setExpiration] = useState<Date | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [policyExpirationRemindersEnabled, setPolicyExpirationRemindersEnabled] = useState<boolean>(false);

  // Email settings for admin users
  const [uploadSuccessEmailEnabled, setUploadSuccessEmailEnabled] = useState<boolean>(true);
  const [attachSuccessOrRejectionEmailsEnabled, setAttachSuccessOrRejectionEmailsEnabled] = useState<boolean>(true);

  // Form validation and submission tracking
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [submissionResults, setSubmissionResults] = useState<{
    successful: string[];
    failed: { unitName: string; error: string }[];
  } | null>(null);

  const relationshipQuery: QueryResult<{ getRelationship: Relationship }> = useQuery(getRelationship, {
    variables: {
      id: relationshipId,
    },
    skip: !relationshipId,
  });

  /* Mixpanel / Calendly events */
  useCalendlyEventListener({
    onDateAndTimeSelected: () =>
      mixpanelEventHandler("Private Policy - Calendly Date Selected", {
        csmId: relationshipQuery?.data?.getRelationship?.csm?.id,
        amId: relationshipQuery?.data?.getRelationship?.am?.id,
        userId: auth.user.id,
        relationshipId,
      }),
    onEventTypeViewed: () =>
      mixpanelEventHandler("Private Policy - Calendly Event Type Viewed", {
        csmId: relationshipQuery?.data?.getRelationship?.csm?.id,
        amId: relationshipQuery?.data?.getRelationship?.am?.id,
        userId: auth.user.id,
        relationshipId,
      }),
    onEventScheduled: (e) =>
      mixpanelEventHandler("Private Policy - Calendly Event Scheduled", {
        csmId: relationshipQuery?.data?.getRelationship?.csm?.id,
        amId: relationshipQuery?.data?.getRelationship?.am?.id,
        userId: auth.user.id,
        relationshipId,
      }),
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onLocationClick = async (selectedLocation) => {
    clearForm();
    if (!selectedLocation) return;

    setLocation(selectedLocation);

    try {
      setIsLoading(true);
      const response = await SetupDataService.getLeasesForLocationId(selectedLocation.id);
      const leasesData = response.data.leases;
      setLeases(leasesData);
      setFilteredLeases(leasesData); // Initialize filtered leases with all leases
      setIsComplianceEnabled(response.data.privatePolicyConfig.isComplianceEnabled);
      setIsTenantPolicyExpirationRemindersEnabled(response.data.privatePolicyConfig.isTenantPolicyExpirationRemindersEnabled);
      setPolicyExpirationRemindersEnabled(response.data.privatePolicyConfig.isTenantPolicyExpirationRemindersEnabledDefault ?? false);
    } catch (error: any) {
      if (error.response?.data?.error !== "Unauthorized") console.error(error);
      setErrors({ other: { name: "", msg: "Error fetching leases for location. Please try again." } });
      if (error.response?.data?.error !== "Unauthorized") Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onLeaseSelection = (selectedLeases: Lease[]) => {
    setSelectedLeases(selectedLeases);

    if (selectedLeases.length === 0) {
      clearFormTenantData();
    } else if (selectedLeases.length === 1) {
      const firstLease = selectedLeases[0];
      setTenantName(firstLease.tenantName);
      setEmail(firstLease.tenantEmail);
      const sameTenantLeases = leases?.filter((lease) => lease.tenantName === firstLease.tenantName) || [];
      setFilteredLeases(sameTenantLeases);
      const unselectedOptions = sameTenantLeases.filter((lease) => !selectedLeases.some((selected) => selected.id === lease.id));
      setDropdownOpen(unselectedOptions.length > 0);
    } else {
      const currentTenantName = selectedLeases[0].tenantName;
      const sameTenantLeases = leases?.filter((lease) => lease.tenantName === currentTenantName) || [];
      const unselectedOptions = sameTenantLeases.filter((lease) => !selectedLeases.some((selected) => selected.id === lease.id));
      setDropdownOpen(unselectedOptions.length > 0);
    }
  };

  const onSubmitPrivatePolicy = async () => {
    setErrors(null);
    setIsLoading(true);
    setSubmissionResults(null);

    const successful: string[] = [];
    const failed: { unitName: string; error: string }[] = [];

    // Upload attachment once if it exists
    let uploadedAttachmentFilename: string | undefined;
    if (attachment) {
      try {
        // TODO: We should validate the file type before uploading
        const isValidAttachment = true;
        if (isValidAttachment) {
          uploadedAttachmentFilename = await AttachmentDataService.directUpload(attachment);
        }
      } catch (error) {
        console.error("Failed to upload attachment:", error);
        setErrors({ attachment: { name: "Uploaded file", msg: "Failed to upload file. Please try again." } });
        setIsLoading(false);
        return;
      }
    }

    // Build out submissions
    const submissions: { unitName?: string; unitId?: number; moveInDate?: Date }[] = [];
    if (unitMatchingMethod === "unitName") {
      submissions.push(...selectedLeases.map((lease) => ({ unitName: lease.unitName, unitId: lease.unitId })));
    } else {
      submissions.push({ moveInDate });
    }

    for (const submission of submissions) {
      try {
        const privatePolicyData: PrivatePolicyData = {
          locationId: location.id,
          tenantName,
          email,
          moveInDate: submission.moveInDate,
          unitName: submission.unitName,
          insuranceCompany: insuranceCompany || null,
          policyNumber,
          policyDeductible: policyDeductible ? Number(policyDeductible.replace(/\D/g, "")) : null,
          expiration: expiration ? dayjs(expiration).toDate() : null,
          attachment,
          policyExpirationRemindersEnabled,
          uploadSuccessEmailEnabled: showAdminSettings ? uploadSuccessEmailEnabled : undefined,
          attachSuccessEmailEnabled: showAdminSettings ? attachSuccessOrRejectionEmailsEnabled : undefined,
          rejectionEmailEnabled: showAdminSettings ? attachSuccessOrRejectionEmailsEnabled : undefined,
          matchedSafeleaseUnitIdOnSubmit: submission.unitId,
        };

        if (attachment && uploadedAttachmentFilename) {
          privatePolicyData.attachment_filename = uploadedAttachmentFilename;
          privatePolicyData.attachment_desired_filename = attachment.name;
          privatePolicyData.attachment_mime_type = attachment.type;
          privatePolicyData.isValidFile = "true";
        }

        const response = await PrivatePolicyDataService.save(privatePolicyData);
        if (response.data.errors) {
          failed.push({ unitName: submission.unitName || "Move-in Date Policy", error: "Validation error" });
        } else {
          successful.push(submission.unitName || "Move-in Date Policy");
        }
      } catch (error) {
        console.error(error);
        failed.push({ unitName: submission.unitName || "Move-in Date Policy", error: "Submission failed" });
        Sentry.captureException(error);
      }
    }

    setSubmissionResults({ successful, failed });
    setIsLoading(false);

    // Set success if at least one submission was successful
    // Also show results dialog even if all submissions failed
    if (successful.length > 0 || failed.length > 0) {
      setIsSuccess(true);
    }
  };

  const validateForm = () => {
    const hasRequiredFields = !!(
      tenantName &&
      email &&
      attachment &&
      policyNumber &&
      expiration &&
      (unitMatchingMethod === "unitName" ? selectedLeases.length > 0 : moveInDate && moveInDate.toString() !== "Invalid Date")
    );
    setIsFormValid(hasRequiredFields);
  };

  const getValidationErrors = () => {
    const fieldErrors: FormErrors = {};

    if (!tenantName) {
      fieldErrors.tenantName = { name: "Tenant name", msg: "Tenant name is required." };
    }
    if (!email) {
      fieldErrors.email = { name: "Tenant email", msg: "Tenant email is required." };
    }
    if (!attachment) {
      fieldErrors.attachment = { name: "Uploaded file", msg: "Policy document is required." };
    }
    if (!policyNumber) {
      fieldErrors.policyNumber = { name: "Policy number", msg: "Policy number is required." };
    }
    if (!expiration) {
      fieldErrors.expiration = { name: "Expiration date", msg: "Expiration date is required." };
    }

    const missingFields: string[] = [];
    for (const key in fieldErrors) {
      if (fieldErrors[key]) {
        missingFields.push(fieldErrors[key].name);
      }
    }

    return { missingFields, fieldErrors };
  };

  const handleDisabledButtonClick = () => {
    const { fieldErrors } = getValidationErrors();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
    }
  };

  // Call validateForm whenever relevant fields change
  useEffect(() => {
    validateForm();

    // Clear attachment error when attachment changes
    if (attachment && errors?.attachment) {
      const { attachment: attachmentError, ...restErrors } = errors;
      setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
    }
  }, [tenantName, email, attachment, policyNumber, expiration, selectedLeases, moveInDate, unitMatchingMethod, errors]);

  const onNewFiles = (files: File[]) => {
    if (files.length === 0) {
      setErrors({ attachment: { name: "Uploaded file", msg: "File is required." } });
    } else if (files.length > 1) {
      setErrors({ attachment: { name: "Uploaded file", msg: "Only one file can be uploaded." } });
    } else {
      setAttachment(files[0]);
      if (errors?.attachment) {
        const { attachment, ...restErrors } = errors;
        setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const clearFormTenantData = () => {
    // Clear selected leases but reset filtered leases to original data
    setSelectedLeases([]);
    setFilteredLeases(leases || []);
    setDropdownOpen(false);

    // Private Policy Details
    setTenantName("");
    setEmail("");
    setMoveInDate(null);
    setInsuranceCompany("");
    setPolicyNumber("");
    setPolicyDeductible("");
    setExpiration(null);
    setAttachment(null);
    setPolicyExpirationRemindersEnabled(false);

    // Reset email settings to defaults
    setUploadSuccessEmailEnabled(true);
    setAttachSuccessOrRejectionEmailsEnabled(true);

    // Reset validation and submission tracking
    setIsFormValid(false);
    setSubmissionResults(null);

    // Clear any field errors
    setErrors(null);
  };

  const clearForm = () => {
    setIsSuccess(false);
    setLocation(null);
    setLeases(null);
    setSelectedLeases([]);
    setFilteredLeases([]);
    setIsComplianceEnabled(false);
    setIsTenantPolicyExpirationRemindersEnabled(false);
    setUnitMatchingMethod("unitName");

    clearFormTenantData();
  };

  return (
    <Fragment>
      <Button onClick={() => setOpen(true)} sx={{ ...ghostButtonNavy, height: "2.5rem" }}>
        Upload Private Policy
      </Button>
      {open && (
        <Dialog
          open={true}
          onClose={handleClose}
          PaperProps={{
            sx: { minWidth: { xs: 300, sm: 600 } },
          }}
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Uploading private policy for tenants</Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {isSuccess && submissionResults ? (
              <>
                <Stack direction="row" justifyContent="center" width={"100%"} mb={4}>
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    alignSelf="center"
                    textAlign="center"
                    spacing={1}
                    sx={{
                      height: "100%",
                      width: "80%",
                    }}
                  >
                    {submissionResults.successful.length > 0 ? (
                      <>
                        <CheckCircleIcon color={"success"} sx={{ fontSize: "60px" }} />
                        <Typography variant="h6">
                          Successfully created private policies for {submissionResults.successful.length} unit
                          {submissionResults.successful.length !== 1 ? "s" : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Units: {submissionResults.successful.join(", ")}
                        </Typography>
                        {submissionResults.failed.length > 0 && (
                          <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                            Failed to create policies for: {submissionResults.failed.map((f) => f.unitName).join(", ")}
                          </Alert>
                        )}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#929292",
                          }}
                        >
                          This may take a few minutes to process.{" "}
                          {email && `A confirmation email will be sent to ${tenantName} at ${email} for each unit.`}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box sx={{ fontSize: "60px", color: "error.main", mb: 2 }}>⚠️</Box>
                        <Typography variant="h6" color="error">
                          Failed to create private policies
                        </Typography>
                        <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                          Failed to create policies for: {submissionResults.failed.map((f) => f.unitName).join(", ")}
                        </Alert>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#929292",
                          }}
                        >
                          Please check your information and try again.
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="center" width={"100%"}>
                  <Button sx={navyButton} onClick={clearForm}>
                    {submissionResults.successful.length > 0 ? "Submit another private policy" : "Try again"}
                  </Button>
                </Stack>
              </>
            ) : (
              <Stack direction="column" spacing={2}>
                <Autocomplete
                  value={location}
                  options={locations}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  getOptionLabel={(option) => (option.name ? `${option.address} - ${option.name}` : option.address)}
                  onChange={(event, value, reason: AutocompleteChangeReason) => onLocationClick(reason === "clear" ? null : value)}
                  renderInput={(params) => <TextField {...params} sx={{ mt: 1 }} label={location ? "Facility" : "Select facility"} />}
                />
                {!!leases && (
                  <>
                    {leases.length > 0 ? (
                      <>
                        {unitMatchingMethod === "unitName" ? (
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            limitTags={3}
                            open={dropdownOpen}
                            onOpen={() => setDropdownOpen(true)}
                            onClose={() => setDropdownOpen(false)}
                            value={selectedLeases}
                            options={filteredLeases}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `Unit ${option.unitName} - ${option.tenantName}`}
                            onChange={(event, value) => onLeaseSelection(value)}
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox checked={selected} />
                                <ListItemText primary={`Unit ${option.unitName} - ${option.tenantName}`} />
                              </li>
                            )}
                            renderInput={(params) => <TextField {...params} label={selectedLeases.length > 0 ? "Units" : "Select units"} />}
                          />
                        ) : (
                          <DatePicker
                            label="Move-in date"
                            value={moveInDate}
                            onChange={(date) => setMoveInDate(dayjs(date).toDate())}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors?.moveInDate,
                                helperText: errors?.moveInDate ? errors.moveInDate.msg : "",
                              },
                            }}
                          />
                        )}
                        <Typography
                          onClick={() => {
                            setUnitMatchingMethod(unitMatchingMethod === "unitName" ? "moveInDate" : "unitName");
                            clearFormTenantData();
                          }}
                          sx={{
                            color: (theme) => theme.palette.grey[600],
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 500,
                            alignSelf: "center",
                            textAlign: "center",
                          }}
                        >
                          {unitMatchingMethod === "unitName" ? "Can't find the unit you're looking for? " : "Don't know your move-in date?"}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ width: "fit-content", alignSelf: "center" }}
                          onClick={() => {
                            setUnitMatchingMethod(unitMatchingMethod === "unitName" ? "moveInDate" : "unitName");
                            clearFormTenantData();
                          }}
                        >
                          {unitMatchingMethod === "unitName" ? "Use move-in date." : "Use unit/space number."}
                        </Button>
                        {((unitMatchingMethod === "unitName" && selectedLeases.length > 0) ||
                          (unitMatchingMethod === "moveInDate" && !!moveInDate && moveInDate.toString() !== "Invalid Date")) && (
                          <>
                            <Typography variant="subtitle1" gutterBottom>
                              Tenant's private policy details
                            </Typography>
                            <Grid container>
                              <Grid item container spacing={{ xs: 0, sm: 2 }} mb={2}>
                                <Grid item container xs={12} sm={6}>
                                  <TextField
                                    value={tenantName}
                                    onChange={(e) => {
                                      setTenantName(e.target.value);
                                      if (errors?.tenantName) {
                                        const { tenantName, ...restErrors } = errors;
                                        setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
                                      }
                                    }}
                                    label="Tenant name"
                                    error={!!errors?.tenantName}
                                    helperText={errors?.tenantName ? errors.tenantName.msg : ""}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item container xs={12} sm={6}>
                                  <TextField
                                    value={email}
                                    onChange={(e) => {
                                      setEmail(e.target.value);
                                      if (errors?.email) {
                                        const { email, ...restErrors } = errors;
                                        setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
                                      }
                                    }}
                                    label="Tenant email"
                                    error={!!errors?.email}
                                    helperText={errors?.email ? errors.email.msg : ""}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                              <Grid item container spacing={{ xs: 0, sm: 2 }} mb={2}>
                                <Grid item container xs={12} sm={6}>
                                  <TextField
                                    value={insuranceCompany}
                                    onChange={(e) => setInsuranceCompany(e.target.value)}
                                    label="Insurance company (optional)"
                                    error={!!errors?.insuranceCompany}
                                    helperText={errors?.insuranceCompany ? errors.insuranceCompany.msg : ""}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item container xs={12} sm={6}>
                                  <TextField
                                    value={policyNumber}
                                    onChange={(e) => {
                                      setPolicyNumber(e.target.value);
                                      if (errors?.policyNumber) {
                                        const { policyNumber, ...restErrors } = errors;
                                        setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
                                      }
                                    }}
                                    label="Policy number"
                                    error={!!errors?.policyNumber}
                                    helperText={errors?.policyNumber ? errors.policyNumber.msg : ""}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                              <Grid item container spacing={{ xs: 0, sm: 2 }} mb={2}>
                                <Grid item container xs={12} sm={6}>
                                  <NumericFormat
                                    value={policyDeductible}
                                    onChange={(e) => setPolicyDeductible(e.target.value)}
                                    allowNegative={false}
                                    fixedDecimalScale
                                    thousandSeparator=","
                                    prefix="$"
                                    decimalScale={2}
                                    fullWidth
                                    error={!!errors?.policyDeductible}
                                    label="Policy Deductible (optional)"
                                    customInput={TextField}
                                  />
                                  {errors?.policyDeductible && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      ml={2}
                                      sx={{ fontFamily: '"Roboto","Helvetica","Arial",sans-serif' }}
                                    >
                                      {errors.policyDeductible.msg}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item container xs={12} sm={6}>
                                  <DatePicker
                                    label="Expiration date"
                                    value={expiration}
                                    onChange={(date) => {
                                      setExpiration(dayjs(date).toDate());
                                      if (errors?.expiration) {
                                        const { expiration, ...restErrors } = errors;
                                        setErrors(Object.keys(restErrors).length > 0 ? restErrors : null);
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        error: !!errors?.expiration,
                                        helperText: errors?.expiration ? errors.expiration.msg : "",
                                        sx: {
                                          ...muiTextField,
                                          marginBottom: 0,
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Grid item container xs={12}>
                                <Card sx={{ borderRadius: 1, width: "100%", mt: 1 }}>
                                  {attachment ? (
                                    <FilePreviews files={[attachment]} deleteFile={() => setAttachment(null)} />
                                  ) : (
                                    <SafeLeaseFileDropzone
                                      containerProps={{
                                        sx: {
                                          borderRadius: 1,
                                          backgroundColor: "#757575",
                                          "&:hover": {
                                            cursor: "pointer",
                                          },
                                        },
                                      }}
                                      uploadButtonProps={{
                                        sx: {
                                          ...inverseNavyButton,
                                          backgroundColor: "white",
                                          "&:hover": {
                                            backgroundColor: "#939ba2",
                                          },
                                        },
                                      }}
                                      accept={{
                                        "application/pdf": [".pdf"],
                                        "image/jpeg": [".jpeg", ".jpg"],
                                        "image/png": [".png"],
                                        "application/msword": [".doc", ".docx"],
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                      }}
                                      handleNewFiles={(files) => onNewFiles(files)}
                                    />
                                  )}
                                </Card>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", mt: 1 }}>
                                  {errors?.attachment && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ fontFamily: '"Roboto","Helvetica","Arial",sans-serif' }}
                                    >
                                      {errors.attachment.msg}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              {/* Trio of possible email-related elements */}
                              {/* #1 Tenant policy expiration reminders - visible to non-admin users when enabled*/}
                              {isComplianceEnabled && isTenantPolicyExpirationRemindersEnabled && !showAdminSettings && (
                                <Grid item container xs={12} mt={1}>
                                  <FormControlLabel
                                    label="Remind tenant 30 days before their policy will expire via email."
                                    control={
                                      <Checkbox
                                        checked={policyExpirationRemindersEnabled}
                                        onChange={(e) => setPolicyExpirationRemindersEnabled(e.target.checked)}
                                      />
                                    }
                                  />
                                </Grid>
                              )}
                              {/* #2 Email settings for admin users */}
                              {showAdminSettings && (
                                <PrivatePolicyEmailSettings
                                  uploadSuccessEmailEnabled={uploadSuccessEmailEnabled}
                                  setUploadSuccessEmailEnabled={setUploadSuccessEmailEnabled}
                                  attachSuccessOrRejectionEmailsEnabled={attachSuccessOrRejectionEmailsEnabled}
                                  setattachSuccessOrRejectionEmailsEnabled={setAttachSuccessOrRejectionEmailsEnabled}
                                  policyExpirationRemindersEnabled={policyExpirationRemindersEnabled}
                                  setPolicyExpirationRemindersEnabled={setPolicyExpirationRemindersEnabled}
                                />
                              )}
                              {/* #3 Calendly button for non-admin users when email settings are disabled */}
                              {!isComplianceEnabled && !showAdminSettings && (
                                <Grid item container justifyContent="center" xs={12} mt={2}>
                                  <CalendlyButton
                                    relationshipId={relationshipId}
                                    label="Want to send 30 day policy expiration notices? Talk to your rep."
                                    linkName="privatePolicy"
                                    hideEndIcon
                                    sx={{
                                      border: "none",
                                      fontWeight: "500",
                                    }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </>
                        )}
                      </>
                    ) : (
                      <Typography p={1}>No leases</Typography>
                    )}
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          {!isSuccess && (
            <DialogActions>
              <Stack direction="column" justifyContent="center" width={"100%"}>
                {isLoading && (
                  <Stack direction="row" justifyContent="center" width={"100%"} mb={1}>
                    <CircularProgress />
                  </Stack>
                )}
                {((unitMatchingMethod === "unitName" && selectedLeases.length > 0) ||
                  (unitMatchingMethod === "moveInDate" && !!moveInDate && moveInDate.toString() !== "Invalid Date")) && (
                  <>
                    {!isLoading && (
                      <Stack direction="row" justifyContent="center" flexGrow={1} mb={1} mx={2}>
                        <Box sx={{ position: "relative", width: "100%" }}>
                          <Button sx={navyButton} onClick={onSubmitPrivatePolicy} fullWidth disabled={!isFormValid}>
                            Submit private {selectedLeases.length ? "policies" : "policy"} (
                            {unitMatchingMethod === "unitName" ? selectedLeases.length : 1})
                          </Button>
                          {!isFormValid && (
                            <Tooltip title={`Missing required fields: ${getValidationErrors().missingFields.join(", ")}`} placement="top">
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  cursor: "pointer",
                                  zIndex: 1,
                                }}
                                onClick={handleDisabledButtonClick}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Stack>
                    )}
                  </>
                )}
                {!!errors?.other?.msg && (
                  <Stack direction="row" justifyContent="center" width={"100%"} mb={1}>
                    <Alert severity="warning" color="error">
                      {errors.other.msg}
                    </Alert>
                  </Stack>
                )}
              </Stack>
            </DialogActions>
          )}
        </Dialog>
      )}
    </Fragment>
  );
}
